import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslationService } from '@/layout/service/translation.service';
import { translateCount } from '@/layout/service/translate-count';
import { GOOD_FEEDBACK_FROM, GOOD_FEEDBACK_MIN_RESPONSES, POOR_FEEDBACK_BELOW, RoomFeedbackHighlight, lowRatings } from '@/cloud/models/feedback.models';
import { RoomListRow } from '@/cloud/models/room-filters';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { MetricTileComponent } from '@/shared/ui/metric-tile/metric-tile.component';
import { RoomRatingComponent } from './room-rating.component';

/**
 * Overview "Room feedback": how users rate the rooms in scope over the last 30 days. The average over every
 * response, how many rooms have poor feedback and how many are rated well, then the rooms behind each count
 * (worst and best first). Each room links to its Feedback tab. Shown only when some room in scope has feedback.
 */
@Component({
    selector: 'app-room-feedback-highlight',
    standalone: true,
    imports: [RouterModule, SectionCardComponent, MetricTileComponent, RoomRatingComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @let s = summary();
        <app-section-card class="room-feedback" icon="fa fa-star" [title]="t()('roomFeedback.title')" [subtitle]="subtitle()">
            <button actions type="button" class="room-feedback__link" (click)="viewRooms.emit()">{{ t()('roomUsage.now.viewRooms') }} <i class="pi pi-arrow-right" aria-hidden="true"></i></button>
            <div class="room-feedback-kpis">
                <app-metric-tile class="feedback-kpi-average" icon="fa fa-star" [label]="t()('feedback.average')" [value]="s.average" unit="/ 5" format="1.1-1" [caption]="count('room.snippet.responses', s.responses)" />
                <app-metric-tile
                    class="feedback-kpi-poor"
                    [class.feedback-kpi--warn]="s.poorTotal > 0"
                    icon="fa fa-face-frown"
                    [label]="t()('roomFeedback.poor')"
                    [value]="s.poorTotal"
                    format="1.0-0"
                    [caption]="t()('roomFeedback.poorCaption').replace('{n}', '' + poorBelow)"
                />
                <app-metric-tile class="feedback-kpi-good" icon="fa fa-face-smile" [label]="t()('roomFeedback.good')" [value]="s.goodTotal" format="1.0-0" [caption]="goodCaption()" />
            </div>
            <div class="room-feedback-lists">
                <section class="room-feedback-list room-feedback-list--poor" [attr.aria-label]="t()('roomFeedback.poor')">
                    <h3><i class="fa fa-face-frown" aria-hidden="true"></i> {{ t()('roomFeedback.poor') }}</h3>
                    @if (s.poor.length) {
                        <ul>
                            @for (room of s.poor; track room.id) {
                                <li>
                                    <a [routerLink]="['/dashboard', namespaceId(), room.id]" [queryParams]="{ tab: 'feedback' }">
                                        <span class="room-feedback__room">
                                            <span class="room-feedback__name">{{ room.displayName }}</span>
                                            @if (room.parentName) {
                                                <span class="room-feedback__sub">{{ room.parentName }}</span>
                                            }
                                        </span>
                                        <app-room-rating [feedback]="room.feedback" />
                                        <span class="room-feedback__detail">{{ lowDetail(room) }}</span>
                                    </a>
                                </li>
                            }
                        </ul>
                        @if (s.poorTotal > s.poor.length) {
                            <p class="room-feedback__more">{{ count('roomUsage.attention.more', s.poorTotal - s.poor.length) }}</p>
                        }
                    } @else {
                        <p class="room-feedback__empty"><i class="pi pi-check-circle" aria-hidden="true"></i> {{ t()('roomFeedback.noPoor').replace('{n}', '' + poorBelow) }}</p>
                    }
                </section>
                <section class="room-feedback-list room-feedback-list--good" [attr.aria-label]="t()('roomFeedback.good')">
                    <h3><i class="fa fa-face-smile" aria-hidden="true"></i> {{ t()('roomFeedback.good') }}</h3>
                    @if (s.good.length) {
                        <ul>
                            @for (room of s.good; track room.id) {
                                <li>
                                    <a [routerLink]="['/dashboard', namespaceId(), room.id]" [queryParams]="{ tab: 'feedback' }">
                                        <span class="room-feedback__room">
                                            <span class="room-feedback__name">{{ room.displayName }}</span>
                                            @if (room.parentName) {
                                                <span class="room-feedback__sub">{{ room.parentName }}</span>
                                            }
                                        </span>
                                        <app-room-rating [feedback]="room.feedback" />
                                        <span class="room-feedback__detail">{{ count('room.snippet.responses', room.feedback?.count ?? 0) }}</span>
                                    </a>
                                </li>
                            }
                        </ul>
                        @if (s.goodTotal > s.good.length) {
                            <p class="room-feedback__more">{{ count('roomUsage.attention.more', s.goodTotal - s.good.length) }}</p>
                        }
                    } @else {
                        <p class="room-feedback__empty">{{ noGoodText() }}</p>
                    }
                </section>
            </div>
        </app-section-card>
    `,
    styles: [
        `
            :host {
                display: block;
                margin-top: 1rem;
            }
            .room-feedback__link {
                display: inline-flex;
                align-items: center;
                gap: 0.5rem;
                padding: 0.4rem 0.6rem;
                border: 0;
                border-radius: var(--content-border-radius);
                background: transparent;
                color: var(--primary-color);
                font: inherit;
                font-size: 0.875rem;
                font-weight: 500;
                cursor: pointer;
                white-space: nowrap;
            }
            .room-feedback__link:hover {
                background: var(--surface-hover);
            }
            .room-feedback__link:focus-visible {
                outline: 2px solid var(--primary-color);
                outline-offset: 2px;
            }
            .room-feedback-kpis {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(min(100%, 9.5rem), 1fr));
                gap: var(--app-space-3);
            }
            .feedback-kpi--warn {
                /* Red border and value; metric-tile reads these tokens. */
                --surface-border: color-mix(in srgb, var(--app-status-offline) 55%, transparent);
                --text-color: var(--app-status-offline-fg);
            }
            .room-feedback-lists {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(min(100%, 20rem), 1fr));
                gap: var(--app-space-4);
                margin-top: var(--app-space-4);
            }
            .room-feedback-list {
                min-width: 0;
            }
            .room-feedback-list h3 {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                margin: 0 0 var(--app-space-2);
                font-size: 0.875rem;
                font-weight: 600;
            }
            .room-feedback-list--poor h3 i {
                color: var(--app-status-offline-fg);
            }
            .room-feedback-list--good h3 i {
                color: var(--app-status-online-fg);
            }
            .room-feedback-list ul {
                display: flex;
                flex-direction: column;
                margin: 0;
                padding: 0;
                list-style: none;
                border: 1px solid var(--surface-border);
                border-radius: var(--content-border-radius);
                overflow: hidden;
            }
            .room-feedback-list li + li {
                border-top: 1px solid var(--surface-border);
            }
            .room-feedback-list a {
                display: grid;
                grid-template-columns: minmax(0, 1fr) auto minmax(0, 9rem);
                align-items: center;
                gap: var(--app-space-3);
                padding: var(--app-space-2) var(--app-space-3);
                color: var(--text-color);
                text-decoration: none;
                font-size: 0.875rem;
            }
            .room-feedback-list a:hover {
                background: var(--surface-hover);
            }
            .room-feedback-list a:focus-visible {
                outline: 2px solid var(--primary-color);
                outline-offset: -2px;
            }
            .room-feedback__room {
                display: flex;
                flex-direction: column;
                min-width: 0;
            }
            .room-feedback__name {
                font-weight: 600;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .room-feedback__sub {
                color: var(--text-color-secondary);
                font-size: 0.75rem;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .room-feedback__detail {
                min-width: 0;
                color: var(--text-color-secondary);
                font-size: 0.8125rem;
                text-align: right;
                overflow-wrap: anywhere;
                font-variant-numeric: tabular-nums;
            }
            .room-feedback__more,
            .room-feedback__empty {
                margin: var(--app-space-2) 0 0;
                color: var(--text-color-secondary);
                font-size: 0.8125rem;
            }
            .room-feedback__empty {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                margin-top: 0;
                padding: var(--app-space-2) var(--app-space-3);
                border: 1px dashed var(--surface-border);
                border-radius: var(--content-border-radius);
            }
            .room-feedback__empty i {
                color: var(--app-status-online-fg);
            }
            @media (max-width: 640px) {
                .room-feedback-list a {
                    grid-template-columns: minmax(0, 1fr) auto;
                    gap: var(--app-space-1) var(--app-space-2);
                }
                .room-feedback__detail {
                    grid-column: 1 / -1;
                    text-align: left;
                }
            }
        `
    ]
})
export class RoomFeedbackHighlightComponent {
    readonly t = inject(TranslationService).t;

    readonly summary = input.required<RoomFeedbackHighlight<RoomListRow>>();
    readonly namespaceId = input<string | null>(null);
    readonly viewRooms = output<void>();

    readonly poorBelow = POOR_FEEDBACK_BELOW;
    readonly goodFrom = GOOD_FEEDBACK_FROM;
    readonly goodMinResponses = GOOD_FEEDBACK_MIN_RESPONSES;

    count(key: string, n: number | null): string {
        return translateCount(this.t(), key, n);
    }

    /** "Last 30 days · 12 rooms rated" (the window is ROOM_FEEDBACK_DAYS). */
    subtitle(): string {
        return `${this.t()('room.snippet.last30')} · ${this.count('roomFeedback.roomsRated', this.summary().ratedRooms)}`;
    }

    /** "rooms averaging 4 ★ or more, 3+ responses". */
    goodCaption(): string {
        return this.t()('roomFeedback.goodCaption')
            .replace('{avg}', '' + this.goodFrom)
            .replace('{min}', '' + this.goodMinResponses);
    }

    /** "No room with 3 or more responses averages 4 stars or more yet." */
    noGoodText(): string {
        return translateCount(this.t(), 'roomFeedback.noGood', this.goodMinResponses, { avg: this.goodFrom });
    }

    /** "2 of 3 rated 1–2 stars". */
    lowDetail(room: RoomListRow): string {
        const f = room.feedback;
        if (!f) return '';
        return translateCount(this.t(), 'roomFeedback.lowOf', f.count, { low: lowRatings(f) });
    }
}
